//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ToastHelper, ConvertHelper, LocalizationHelper, LogHelper, CommonHelper } from '../../helpers'

import { Enums, OpChangeState } from '../../models/common/enums'
import { GetViewColumnOptions, OpView } from '../../models/core/op-view'
import { OpViewColumn } from '../../models/core/op-view-column'

import { OpDataTypeInGroup } from '../../models/core/op-data-type-in-group'
import { OpAlarmGroup } from '../../models/core/op-alarm-group'
import { OpAlarmGroupData } from '../../models/core/op-alarm-group-data'
import { OpDataList } from '../../models/util'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'

import { CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { DATA_TYPE_GROUP_SERVICE_NAME, DataTypeGroupService } from '../data-type'
import { SYSTEM_SERVICE_NAME, $Q_SERVICE_NAME, SystemService } from '../system'

import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'

//#region Public Types

export const ALARM_GROUP_SERVICE_NAME = 'AlarmGroupService'
export const ALARM_GROUP_SERVICE_PROVIDER_NAME = 'AlarmGroupServiceProvider'

//#endregion

export class AlarmGroupService {

    //#region Consts

    public static get FILE_NAME_PREFIX(): string { return 'alarm-groups' }
    public static get LOG_PREFIX(): string { return '[SIMAX.Module.AlarmGroup]' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdAlarmGroup }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Alarm_Group }

    //#endregion

    //#region Ctor

    public constructor(
        private $q: ng.IQService,
        private dataTypeGroupService: DataTypeGroupService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    public getAlarmGroup(idAlarmGroup: number): JQueryPromise<OpAlarmGroup> {
        return this.userSessionService
            .getDataProvider().AlarmGroup
            .getAlarmGroup(idAlarmGroup)
            .then((alarmGroup: OpAlarmGroup) => {
                if (alarmGroup != null) {
                    alarmGroup.dataList = this.createDataListFromAlarmGroup(alarmGroup)
                    return alarmGroup
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(AlarmGroupService.LOG_PREFIX, `Unable to load alarm group for id ${idAlarmGroup}`, error)

                return null
            })
    }

    public saveAlarmGroup(alarmGroupToSave: OpAlarmGroup, isBulkSave: boolean = false): JQueryPromise<OpAlarmGroup> {
        return this.userSessionService
            .getDataProvider().AlarmGroup
            .saveAlarmGroup(alarmGroupToSave)
            .then((savedObject: OpAlarmGroup) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, AlarmGroupService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.alarmGroupSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, AlarmGroupService.SIMAX_MODULE_NAME))
                LogHelper.warn(AlarmGroupService.LOG_PREFIX, `Unable to save alarm group with id ${alarmGroupToSave.idAlarmGroup}.`, error)

                return $.Deferred().reject(error).promise()
            })
    }

    public deleteAlarmGroup(selectedItemId: number): JQueryPromise<void> {
        const toDelete: OpAlarmGroup = new OpAlarmGroup(null)

        toDelete.idAlarmGroup = selectedItemId

        return this.userSessionService
            .getDataProvider().AlarmGroup
            .deleteAlarmGroup(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, AlarmGroupService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, AlarmGroupService.SIMAX_MODULE_NAME))
                LogHelper.warn(AlarmGroupService.LOG_PREFIX, `Unable to delete alarm group for id ${selectedItemId}`, error)
            })
    }

    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null): CreateEditInitParams {
        return {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: AlarmGroupService.REFERENCE_TYPE,
            moduleName: AlarmGroupService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject,
            referenceObjectId,
        }
    }

    public openDetails(alarmGroupId: number, pageItemsIds: number[]): void {
        this.systemService.openDetails({
            moduleName: AlarmGroupService.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: alarmGroupId,
        })
    }

    public getBasicParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            AlarmGroupService.SIMAX_MODULE_NAME
        )
    }

    //#endregion

    //#region Helper

    public createDataListFromAlarmGroup(alarmGroup: any): OpDataList<OpAlarmGroupData> {

        const dataList: OpDataList<OpAlarmGroupData> = new OpDataList<OpAlarmGroupData>()

        const idDistributor: OpAlarmGroupData = new OpAlarmGroupData({
            ID_DATA_TYPE: Enums.DataType.HELPER_ID_DISTRIBUTOR,
            INDEX_NBR: 0,
            VALUE: alarmGroup.idDistributor ? alarmGroup.idDistributor : null,
            OpState: OpChangeState.Loaded
        })

        dataList.push(idDistributor)

        const promises: any[] = []
        promises.push(this.createAlarmGroupDataFromDynamicValue(alarmGroup.dynamicValues, Enums.DataType.HELPER_NAME))
        promises.push(this.createAlarmGroupDataFromDynamicValue(alarmGroup.dynamicValues, Enums.DataType.HELPER_DESCRIPTION))
        promises.push(this.createAlarmGroupDataFromDynamicValue(alarmGroup.dynamicValues, Enums.DataType.HELPER_IS_ENABLED))

        this.$q.all(promises)
            .then((alarmGroupData: any[]) => {
                if (alarmGroupData != null) {
                    alarmGroupData.forEach((data: OpAlarmGroupData) => {
                        dataList.push(data)
                    })
                }

                return dataList
            })

        return dataList
    }

    private createAlarmGroupDataFromDynamicValue(alarmGroupDynamicValues: any, dataType: Enums.DataType): JQueryPromise<OpAlarmGroupData> {
        return this.getViewColumnName(dataType)
            .then((viewColumnName: string) => {
                const dynamicValue: any = _.find(alarmGroupDynamicValues, ((dynamicValue: any) => dynamicValue.Key === viewColumnName))

                const alarmGroupData: OpAlarmGroupData = new OpAlarmGroupData({
                    ID_DATA_TYPE: dataType,
                    INDEX_NBR: 0,
                    VALUE: dynamicValue.Value ? dynamicValue.Value : null,
                    OpState: OpChangeState.Loaded
                })

                return alarmGroupData
            })
    }

    private getViewColumnName(dataType: Enums.DataType): JQueryPromise<string> {
        return this.userSessionService
            .getDataProvider()
            .View
            .getActiveView(Enums.ReferenceType.IdAlarmGroup)
            .then((view: OpView) => {
                const column: OpViewColumn = view.getViewColumn(new GetViewColumnOptions(dataType))
                return column != null ? column.name : null
            })
    }
    //#endregion
}

export class AlarmGroupServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        $Q_SERVICE_NAME,
        DATA_TYPE_GROUP_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (
            $q: ng.IQService,
            dataTypeGroupService: DataTypeGroupService,
            systemService: SystemService,
            userSessionService: UserSessionService
        ) {
            return new AlarmGroupService($q, dataTypeGroupService, systemService, userSessionService)
        }]

    //#endregion
} 